import React, { useCallback, useLayoutEffect, useRef } from 'react';
import './SheetInputs.scss';

const scrollHintToEnd = el => {
  if (!el) return;
  el.scrollLeft = el.scrollWidth - el.clientWidth;
};

/**
 * Controlled sheet cell text/number input — borderless, fills the cell.
 * Long hints scroll horizontally and default to showing the end of the label.
 */
const SheetTextInput = ({
  value = '',
  onChange,
  type = 'text',
  disabled = false,
  placeholder = '',
  title,
  textAlign = 'left',
  inline = false,
  listId,
  datalistOptions,
  'aria-label': ariaLabel
}) => {
  const hintRef = useRef(null);
  const isDate = type === 'date';
  const isEmpty = value === '' || value == null;
  const showHint = isEmpty && Boolean(placeholder) && !disabled;

  const alignHintToEnd = useCallback(() => {
    scrollHintToEnd(hintRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!showHint) return;
    alignHintToEnd();
  }, [showHint, placeholder, alignHintToEnd]);

  const handleHintWheel = e => {
    const el = hintRef.current;
    if (!showHint || !el || el.scrollWidth <= el.clientWidth) return;

    e.preventDefault();
    e.stopPropagation();
    el.scrollLeft += e.deltaY;
  };

  return (
    <div
      className={[
        'dochub-sheet-input-wrap',
        inline ? 'is-inline' : '',
        isDate && isEmpty ? 'is-date-empty' : ''
      ]
        .filter(Boolean)
        .join(' ')}
      onWheel={handleHintWheel}
      onMouseEnter={alignHintToEnd}
    >
      {showHint && (
        <div
          ref={hintRef}
          className="dochub-sheet-hint"
          style={{
            textAlign,
            justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start'
          }}
          aria-hidden="true"
        >
          <span className="dochub-sheet-hint__text">{placeholder}</span>
        </div>
      )}
      <input
        className="dochub-sheet-input"
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        value={value}
        disabled={disabled}
        placeholder=""
        title={title}
        list={listId || undefined}
        aria-label={ariaLabel || placeholder || title}
        style={{ textAlign }}
        onChange={e => onChange?.(e.target.value)}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onWheel={showHint ? handleHintWheel : undefined}
      />
      {listId && Array.isArray(datalistOptions) && datalistOptions.length > 0 && (
        <datalist id={listId}>
          {datalistOptions.map(option => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
    </div>
  );
};

export default SheetTextInput;
